import {
    useCallback,
    useEffect,
    useRef,
    useState,
    useMemo,
    type ReactNode,
} from 'react';
import { useTracker } from '@archipro-website/tracker';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { useAtomValue } from 'jotai';
import { Check, CheckCircle2, Loader2, Pencil, X } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';
import { Button } from '@archipro/rocco/components/button';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { Input } from '@rocco/components/input';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@rocco/components/form';
import { PopoverAnchor } from '@rocco/components/popover';
import { RoccoLink } from '@rocco/components/link/RoccoLink';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import { Textarea } from '@rocco/components/textarea';
import { usePlaceFinder } from '@modules/location/hook/use-place-finder';
import { getClosestBasicPlace } from '@modules/location/util';
import { trackEnquiryFormEngagement } from '~/modules/tracking/util/trackEnquiryFunnelStage';
import { useUserLoggedIn } from '@modules/user';
import { PostcodePopover } from './PostcodePopover';
import { FileAttachment } from './FileAttachment';
import { fileStorage } from '~/utils/file-storage'; // your IndexedDB wrapper
import { useFeature } from '~/modules/root';
import { getInboxURL } from '~/modules/chat/utils/url';
import { useGetBuildHubsList } from '@ui-rocco/hooks/use-build-hubs';
import { BuildHubSelector } from './build-hub/BuildHubSelector';
import { EnquiryPreview } from './build-hub/EnquiryPreview';
import type { Branch } from '../util/extract-branch-from-network.server';
import {
    useEnquiry,
    type EnquiryProfessional,
    type UseEnquiryProps,
} from '../hooks/use-enquiry';
import { useBuildHubFieldSelection } from '../hooks/use-build-hub-field-selection';
import { useEnquiryFormForPage } from '../hooks/use-enquiry-form-for-page';
import type {
    EnquiryFormSubmissionContext,
    PublicEnquiryForm,
    PublicEnquiryFormField,
} from '../types/public-enquiry-form';
import {
    applyFormMessageSettings,
    buildFormContext,
} from '../domain/configured-enquiry';
import {
    readCreatedEnquiryId,
    readOfferedDownloads,
} from '../domain/enquiry-outcome';
import { useConfiguredEnquiry } from '../hooks/use-configured-enquiry';
import { CustomFormFields } from './CustomFormFields';
import { EnquiryBookingStep } from './EnquiryBookingStep';
import { EnquiryDownloadsStep } from './EnquiryDownloadsStep';
import type {
    EnquirerType,
    EnquiryFormTrigger,
    EnquiryProjectKind,
} from 'generated/graphql';
import {
    ENQUIRER_TYPE_OPTIONS,
    FIELD_QUESTIONS,
    PROJECT_KIND_OPTIONS,
} from '~/modules/business-manager/forms/types';

export interface EnquiryFormProps {
    className?: string;
    /**
     * Shape of the attached-file chips. `compact` is the pill the enquiry modal
     * uses; the default is the wider chip the older inline forms were laid out
     * around.
     */
    attachmentVariant?: 'default' | 'compact';
    /** The professional to submit an enquiry to. */
    professional: EnquiryProfessional & { CustomEnquiryMessage?: string };
    /** The type of the Library Item (professional, product, project, article). */
    itemType: UseEnquiryProps['itemType'];
    /** The ID of the Library Item (professional, product, project, article). */
    siteTreeID: UseEnquiryProps['siteTreeID'];
    /** Bulk enquiry: SiteTree ids of every selected item. */
    siteTreeIDs?: UseEnquiryProps['siteTreeIDs'];
    /** The source of the enquiry. */
    source: UseEnquiryProps['source'];
    /** The project details if the enquiry is for a project. Used for tracking. */
    relatedItem?: UseEnquiryProps['relatedItem'];
    /**
     * Include a branch dropdown if the professional has branches. Defaults to `false`.
     */
    includeBranch?: boolean;
    /**
     * Ask for a suburb / postcode. Defaults to `true` — this form predates the
     * revamp and the legacy surfaces still collect it. The revamped Contact
     * tab opts out: the professional gets the project's location from the
     * attached BuildHub project, not the reader's home address.
     */
    collectLocation?: boolean;
    /** The enquiry form is in a modal. Defaults to `false`. */
    isModal?: boolean;
    /**
     * Show the professional's custom enquiry message from their settings. Defaults to `true`.
     */
    showCustomEnquiryMessage?: boolean;
    /** Override the default submit button text. Defaults to `"Submit"`. */
    submitButtonText?: string;
    /** Disable the submit button. Defaults to `false`. */
    disableSubmit?: boolean;
    /**
     * Rendered directly above the submit button, e.g. an embed's security
     * check and the reason its last send failed.
     */
    beforeSubmit?: ReactNode;
    /** Play the success animation and show the success message. Defaults to `true`. */
    showSuccessMessage?: boolean;
    /** Disable auto-focus on form fields. Defaults to `false`. */
    disableAutoFocus?: boolean;
    /** Whether the mobile keyboard is visible. Used for mobile Safari keyboard handling. */
    isKeyboardVisible?: boolean;
    /** The name of the item being enquired about (e.g. product name). Used to pre-fill the message. */
    itemTitle?: string;
    /** The CTA label that opened the form. Used to tailor the pre-filled message. */
    ctaLabel?: string;
    /** Called when the enquiry is successfully submitted. */
    onSuccess?: UseEnquiryProps['onSuccess'];
    /** Called when the enquiry fails to submit. */
    onFailure?: UseEnquiryProps['onFailure'];
    /** Called when the user cancels login. */
    onAuthCancel?: UseEnquiryProps['onAuthCancel'];
    /** Called when the success screen close action is clicked. */
    onSuccessClose?: () => void;
    /**
     * Hands the sent enquiry to the host instead of showing the success screen
     * or the booking step, e.g. the booking gate, which books the time itself.
     */
    onSubmitted?: (enquiryId: number) => void;
    /**
     * The configured enquiry form to render (Business Manager Forms). When
     * omitted the form resolves the professional's assignment for `itemType`
     * itself; pass `null` to force the legacy flat settings (e.g. bulk).
     */
    enquiryForm?: PublicEnquiryForm | null;
    /**
     * Which button's assignment to resolve when `enquiryForm` is omitted.
     * Defaults to Enquire; Book a meeting entry points pass BookMeeting.
     */
    trigger?: EnquiryFormTrigger;
    /** Where the submission comes from. Defaults to `archipro`. */
    channel?: EnquiryFormSubmissionContext['channel'];
    /**
     * Draws the form for the Business Manager's preview. Nothing is sent: a
     * send that passes validation calls `onSubmit` instead, and
     * `declineMessage` shows the notice a matching message rule would.
     */
    preview?: { onSubmit: () => void; declineMessage?: string | null };
    /** Host page details for embedded forms, stamped on the enquiry. */
    submissionSource?: Pick<
        EnquiryFormSubmissionContext,
        'sourceUrl' | 'sourceParams' | 'captchaToken'
    >;
}

const COMPLETION_TICK_VALIDATION_FIELDS = [
    'name',
    'email',
    'phoneNumber',
    'postcode',
] as const;

/**
 * Form for submitting an enquiry to a professional. This component handles
 * form validation, user submission, and displays the login/signup form if the
 * user submits but is not logged in.
 */
export const EnquiryForm = ({
    professional: baseProfessional,
    enquiryForm: explicitEnquiryForm,
    trigger,
    channel,
    submissionSource,
    siteTreeIDs,
    itemType,
    ...props
}: EnquiryFormProps) => {
    // Bulk enquiries fan out to many professionals, so no single form applies.
    const resolved = useEnquiryFormForPage({
        professionalId: baseProfessional.ID,
        itemType,
        trigger,
        productId: itemType === 'product' ? props.siteTreeID : undefined,
        enabled: explicitEnquiryForm === undefined && !siteTreeIDs?.length,
    });
    const enquiryForm =
        explicitEnquiryForm === undefined ? resolved.form : explicitEnquiryForm;

    // A configured form owns the message settings the flat professional
    // fields used to carry, so it overrides them for this render only.
    const professional = useMemo(
        () => applyFormMessageSettings(baseProfessional, enquiryForm),
        [baseProfessional, enquiryForm],
    );
    const formContext = useMemo(
        () => buildFormContext(enquiryForm, { channel, submissionSource }),
        [enquiryForm, channel, submissionSource],
    );

    if (resolved.isOff) {
        return (
            <div
                className={cn(
                    'rounded-xl border border-gray-200 bg-[#fff] p-6 text-base text-gray-600',
                    props.className,
                )}
                role="status"
            >
                {baseProfessional.Title} is not accepting enquiries from this
                page right now.
            </div>
        );
    }

    return (
        <EnquiryFormInner
            key={enquiryForm?.id ?? 'legacy'}
            {...props}
            professional={professional}
            itemType={itemType}
            siteTreeIDs={siteTreeIDs}
            enquiryForm={enquiryForm}
            formContext={formContext}
        />
    );
};

type EnquiryFormInnerProps = Omit<
    EnquiryFormProps,
    'channel' | 'submissionSource'
> & {
    formContext?: EnquiryFormSubmissionContext;
};

const EnquiryFormInner = ({
    className,
    attachmentVariant = 'default',
    professional,
    enquiryForm,
    formContext,
    itemType,
    siteTreeID,
    siteTreeIDs,
    source,
    relatedItem,
    includeBranch = false,
    collectLocation: collectLocationProp = true,
    isModal = false,
    showCustomEnquiryMessage = true,
    submitButtonText = 'Submit',
    disableSubmit = false,
    beforeSubmit,
    showSuccessMessage = true,
    disableAutoFocus = false,
    isKeyboardVisible = false,
    itemTitle,
    ctaLabel,
    onSuccess,
    onFailure,
    onAuthCancel,
    onSuccessClose,
    onSubmitted,
    preview,
}: EnquiryFormInnerProps) => {
    const { user, isLoggedIn } = useUserLoggedIn();
    // The form's own questions (enquiry type, "I am a…", project kind, custom
    // fields) and which of the fixed ones it switches off or renames.
    const configured = useConfiguredEnquiry(enquiryForm ?? null, {
        allowLocation: collectLocationProp,
    });
    const { rules, answers, errors: configuredErrors } = configured;
    const enquiryTypeOptions = rules.enquiryTypes;
    const selectedType = configured.selectedType;
    const typeError = !!configuredErrors.enquiryType;
    const { asksEnquirerType, asksProjectKind } = rules;
    const { enquirerType, projectKind, fieldAnswers } = answers;
    const {
        collectLocation,
        messageEnabled,
        messageRequired,
        locationLabel,
        customFields,
        requiredCustomFields,
        consentText,
    } = rules;
    const allowsAttachments = rules.attachmentsEnabled;
    const { isDesktop } = useAtomValue(uiContextAtomDisplayMode);
    const isBuildHubEnabled = useFeature('buildhub');
    const isBuildHubEnquiryProjectDetailsEnabled = useFeature(
        'buildhub_enquiry_project_details',
    );
    const newChatUI2025Enabled = useFeature('new_chat_ui_2025');
    const canIncludeProjectDetails =
        isBuildHubEnabled &&
        isBuildHubEnquiryProjectDetailsEnabled &&
        isLoggedIn;
    const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
    // Set at submit time when routing lands on a booking link; consumed on
    // success so the user goes straight on to picking a time instead of the
    // generic "message sent" screen.
    const pendingBookingRef = useRef<PendingBooking | null>(null);
    const [bookingHandoff, setBookingHandoff] = useState<PendingBooking | null>(
        null,
    );
    /** Sent with `onSubmitted` set: waiting for the enquiry id to hand over. */
    const [awaitingHandOff, setAwaitingHandOff] = useState(false);
    // Where the confirmation (and any download links) went, captured at submit
    // because the form resets on success.
    const submittedEmailRef = useRef<string | null>(null);
    const [postcodeSearch, setPostcodeSearch] = useState('');
    const [isPostcodeSearchOpen, setIsPostcodeSearchOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [files, setFiles] = useState<File[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const postcodeInputRef = useRef<HTMLInputElement | null>(null);
    const formRef =
        useRef<ReturnType<typeof useForm<z.infer<typeof formSchema>>>>(
            undefined,
        );

    const [buildHubs, isBuildHubsLoading, fetchBuildHubs] =
        useGetBuildHubsList();
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isMessageFocused, setIsMessageFocused] = useState(false);
    const [submitNudgeKey, setSubmitNudgeKey] = useState(0);

    useEffect(() => {
        if (!canIncludeProjectDetails) {
            return;
        }

        if (!buildHubs && !isBuildHubsLoading) {
            fetchBuildHubs();
        }
    }, [
        buildHubs,
        fetchBuildHubs,
        canIncludeProjectDetails,
        isBuildHubsLoading,
    ]);

    // Add a session ID for file storage
    const sessionId = useMemo(
        () => `${professional.ID}-${siteTreeID || ''}`,
        [professional.ID, siteTreeID],
    );

    const playSuccessAnimation = useCallback(() => {
        setIsSubmitSuccess(true);
    }, []);

    const {
        defaultFormValues,
        formSchema,
        state,
        fetcherData,
        isAutoSubmitting,
        autoSubmitDelay,
        submitEnquiry,
        saveEnquirySession,
        saveSuburbToProfile,
        messagePlaceholder,
    } = useEnquiry({
        professional,
        itemType,
        siteTreeID,
        siteTreeIDs,
        source,
        relatedItem,
        includeBranch,
        collectLocation,
        messageEnabled,
        messageRequired,
        isModal,
        itemTitle,
        ctaLabel,
        formContext,
        onSuccess: () => {
            formRef.current?.reset();
            setFiles([]);
            configured.reset();
            fileStorage.clearFiles(sessionId).catch(console.error);
            const pendingBooking = pendingBookingRef.current;
            pendingBookingRef.current = null;
            if (onSubmitted) {
                setAwaitingHandOff(true);
            } else if (pendingBooking) {
                setBookingHandoff(pendingBooking);
            } else {
                playSuccessAnimation();
            }
            onSuccess?.();
        },
        onFailure,
        onAuthCancel,
    });

    const createdEnquiryId = useMemo(
        () => readCreatedEnquiryId(fetcherData),
        [fetcherData],
    );

    // The enquiry fetcher is shared by every form on the page, so its data can
    // hold an earlier enquiry; only hand off once this form's send succeeded.
    useEffect(() => {
        if (!awaitingHandOff || !createdEnquiryId) return;
        setAwaitingHandOff(false);
        onSubmitted?.(createdEnquiryId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [awaitingHandOff, createdEnquiryId]);

    // Signed links the submission earned, when the form offers downloads.
    const offeredDownloads = useMemo(
        () => readOfferedDownloads(fetcherData),
        [fetcherData],
    );
    const successEmail =
        submittedEmailRef.current ?? (isLoggedIn ? user.Email : null) ?? null;

    const successInboxLink = useMemo(() => {
        if (!newChatUI2025Enabled) {
            return { to: '/member/inbox', isNonRemixRoute: true };
        }

        let conversationID: number | null = null;
        if (
            fetcherData &&
            'success' in fetcherData &&
            fetcherData.data?.__typename === 'CreateEnquiryResult' &&
            fetcherData.data.Conversation?.ID
        ) {
            const parsed = Number(fetcherData.data.Conversation.ID);
            conversationID =
                Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
        }

        return {
            to: getInboxURL(null, conversationID),
            isNonRemixRoute: false,
        };
    }, [newChatUI2025Enabled, fetcherData]);

    useEffect(() => {
        if (isAutoSubmitting && !isModal) {
            containerRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [isAutoSubmitting, isModal]);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        disabled: state !== 'idle' || autoSubmitDelay,
        mode: 'onChange',
        defaultValues: defaultFormValues,
    });

    // Store form in ref so it can be accessed in callbacks
    formRef.current = form;

    // Funnel stage: the user started filling the form (once per rendered
    // form). Watch for genuine input events (type 'change') rather than
    // formState.isDirty — several effects restore or clear values with
    // shouldDirty: true and must not count as engagement.
    const tracker = useTracker();
    const isFormEngagementTracked = useRef(false);
    useEffect(() => {
        const subscription = form.watch((_values, { type }) => {
            if (type !== 'change' || isFormEngagementTracked.current) {
                return;
            }
            isFormEngagementTracked.current = true;
            trackEnquiryFormEngagement(tracker, {
                professionalName: professional.Title,
                itemID: siteTreeID,
                itemType,
                source,
                professionalID: professional.ID,
            });
        });
        return () => subscription.unsubscribe();
    }, [form, tracker, professional, itemType, siteTreeID, source]);

    // Live routing prediction. When the visitor's answers match a rule that shows a
    // message, the message replaces the message box and submit button straight away,
    // so nobody writes an enquiry that was never going to be sent.
    const watchedRegion = form.watch('postcode')?.value?.region ?? null;
    const { predict } = configured;
    const previewDecline = preview?.declineMessage ?? null;
    const declineMessage = useMemo(
        () => previewDecline ?? predict(watchedRegion)?.message ?? null,
        [predict, watchedRegion, previewDecline],
    );

    // Get selected BuildHub ID for field selection (after form is initialized)
    const selectedBuildHubId = form.watch('buildHubId');
    const [fieldSelection, setFieldSelection] =
        useBuildHubFieldSelection(selectedBuildHubId);

    useEffect(() => {
        if (canIncludeProjectDetails || !selectedBuildHubId) {
            return;
        }

        form.setValue('buildHubId', undefined, { shouldDirty: true });
        saveEnquirySession({
            ...form.getValues(),
            buildHubId: undefined,
        });
    }, [
        canIncludeProjectDetails,
        form,
        saveEnquirySession,
        selectedBuildHubId,
    ]);

    const {
        changeKeyword: findPlace,
        dropdownOptions: placeOptions,
        loading: isPlaceOptionsLoading,
    } = usePlaceFinder({
        limit: 10,
        debounceMs: 250,
    });

    const autoSelectBranch = useCallback(
        (postcode: (typeof placeOptions)[number]) => {
            if (
                !includeBranch ||
                !professional.branchOptions.length ||
                form.getValues('branchId')
            ) {
                return;
            }
            const branches = professional.branchOptions.map((p) => p.value);
            const closestPlace = getClosestBasicPlace(
                postcode.value,
                branches,
            ) as Branch | undefined;
            const closestBranch = branches.find((branch) => {
                return branch.branchId === closestPlace?.branchId;
            });
            if (closestBranch) {
                form.setValue('branchId', closestBranch.branchId.toString());
            }
        },
        [includeBranch, professional, form],
    );

    const selectPostcode = useCallback(
        (place: (typeof placeOptions)[number] | undefined) => {
            if (place) {
                form.setValue('postcode', place, { shouldDirty: true });
                setPostcodeSearch(place.header);
                autoSelectBranch(place);
                saveSuburbToProfile(place);
            } else {
                // @ts-expect-error - force postcode to be null.
                form.setValue('postcode', null, { shouldDirty: true });
                setPostcodeSearch('');
            }
            setIsPostcodeSearchOpen(false);
            saveEnquirySession({
                ...form.getValues(),
                postcode: place,
            });
        },
        [form, autoSelectBranch, saveEnquirySession, saveSuburbToProfile],
    );

    /**
     * Automatically select the first postcode option if the user has typed a
     * postcode, there are options available, and postcode is not set yet.
     */
    const autoSelectPostcode = useCallback(() => {
        if (
            postcodeSearch &&
            placeOptions.length &&
            !form.getValues('postcode')
        ) {
            selectPostcode(placeOptions[0]);
            form.clearErrors('postcode');
        }
    }, [postcodeSearch, placeOptions, form, selectPostcode]);

    /**
     * Select the first available postcode option from the dropdown options.
     * Does not change the selection if the selected postcode is in the dropdown.
     */
    const selectAvailablePostCode = useCallback(() => {
        if (placeOptions.length && !isPlaceOptionsLoading) {
            const selectedPlace = placeOptions.find(
                (place) =>
                    place.value.addressID ===
                    form.getValues('postcode')?.value.addressID,
            );
            if (selectedPlace) {
                selectPostcode(selectedPlace);
            } else {
                selectPostcode(placeOptions[0]);
            }
        }
    }, [placeOptions, isPlaceOptionsLoading, form, selectPostcode]);

    /** Whether all required fields except the message are filled. */
    const areOtherFieldsFilled = useCallback(() => {
        const v = form.getValues();
        const basicFilled = !!(
            v.name?.trim() &&
            v.email?.trim() &&
            v.phoneNumber?.trim() &&
            // Only where it is asked for. Counting a field the form never
            // renders leaves this permanently false, and the submit nudge it
            // gates never fires.
            (!collectLocation || v.postcode)
        );
        if (includeBranch && professional.branchOptions?.length) {
            return basicFilled && !!v.branchId;
        }
        return basicFilled;
    }, [
        form,
        includeBranch,
        collectLocation,
        professional.branchOptions?.length,
    ]);

    const handleFileChange = useCallback(
        async (newFiles: File[]) => {
            setFiles(newFiles);
            await fileStorage.saveFiles(sessionId, newFiles);
            saveEnquirySession(form.getValues(), newFiles);

            if (
                newFiles.length > 0 &&
                areOtherFieldsFilled() &&
                form.formState.dirtyFields.message
            ) {
                setSubmitNudgeKey((k) => k + 1);
            }
        },
        [sessionId, form, saveEnquirySession, areOtherFieldsFilled],
    );

    useEffect(() => {
        setHighlightedIndex(placeOptions.length > 0 ? 0 : -1);
    }, [placeOptions]);

    useEffect(() => {
        // Auto select the first postcode option if the input is not focused and no value is set.
        if (document.activeElement !== postcodeInputRef.current) {
            autoSelectPostcode();
        }
    }, [autoSelectPostcode]);

    useEffect(() => {
        if (!form.formState.isDirty && !form.formState.isSubmitted) {
            // This is required to load the saved session data.
            form.reset(defaultFormValues);

            // Revalidate restored values so completion ticks reflect actual validity.
            const restoredFieldsToValidate =
                COMPLETION_TICK_VALIDATION_FIELDS.filter((fieldName) => {
                    if (fieldName === 'postcode' && !collectLocation) {
                        return false;
                    }
                    const value = defaultFormValues[fieldName];

                    if (typeof value === 'string') {
                        return !!value.trim();
                    }

                    return !!value;
                });

            if (restoredFieldsToValidate.length) {
                void form.trigger(restoredFieldsToValidate);
            }

            const restoredPostcode = form.getValues('postcode');
            if (!postcodeSearch && restoredPostcode) {
                setPostcodeSearch(restoredPostcode.header);
            }

            // Restore files from IndexedDB.
            fileStorage.getFiles(sessionId).then((restoredFiles) => {
                if (restoredFiles.length) {
                    setFiles(restoredFiles);
                }
            });
        }
    }, [collectLocation, defaultFormValues, form, postcodeSearch, sessionId]);

    if (isPreviewOpen) {
        const selectedBuildHub = buildHubs?.find(
            (b) => String(b.ID) === selectedBuildHubId,
        );
        const message = form.watch('message');

        if (selectedBuildHub) {
            return (
                <div className={cn('relative h-full', className)}>
                    <EnquiryPreview
                        buildHub={selectedBuildHub}
                        message={message}
                        onBack={() => setIsPreviewOpen(false)}
                        fieldSelection={fieldSelection}
                    />
                </div>
            );
        }
    }

    if (bookingHandoff) {
        return (
            <div ref={containerRef} className={cn('relative', className)}>
                <EnquiryBookingStep
                    sessionId={bookingHandoff.sessionId}
                    enquiryId={createdEnquiryId}
                    prefill={{
                        clientName: bookingHandoff.clientName,
                        clientEmail: bookingHandoff.clientEmail,
                    }}
                    onClose={() => {
                        setBookingHandoff(null);
                        onSuccessClose?.();
                    }}
                />
                {offeredDownloads.length > 0 ? (
                    <EnquiryDownloadsStep
                        compact
                        className="mt-6"
                        downloads={offeredDownloads}
                        email={successEmail}
                    />
                ) : null}
            </div>
        );
    }

    // A gated download form: the files replace the "message sent" screen.
    if (isSubmitSuccess && offeredDownloads.length > 0) {
        return (
            <div ref={containerRef} className={cn('relative', className)}>
                <EnquiryDownloadsStep
                    downloads={offeredDownloads}
                    email={successEmail}
                    message={enquiryForm?.successMessage}
                    onClose={
                        onSuccessClose ??
                        (() => {
                            setIsSubmitSuccess(false);
                        })
                    }
                />
            </div>
        );
    }

    return (
        <div ref={containerRef} className={cn('relative', className)}>
            <div
                className={cn(
                    'space-y-8',
                    ((isSubmitSuccess && showSuccessMessage) ||
                        isAutoSubmitting) &&
                        'invisible',
                )}
            >
                {showCustomEnquiryMessage &&
                    !!professional.CustomEnquiryMessage && (
                        <div className="text-base whitespace-pre-wrap">
                            {professional.CustomEnquiryMessage}
                        </div>
                    )}
                <Form {...form}>
                    {/* HubSpot / edge scripts may add e.g. data-hs-cf-bound on the server HTML */}
                    <form
                        suppressHydrationWarning
                        id="enquiry-form"
                        // Zod + validateFieldAnswers own validation; native
                        // browser checks (e.g. type="url" rejecting a
                        // scheme-less domain) would disagree with them.
                        noValidate
                        onSubmit={form.handleSubmit(
                            (data) => {
                                if (state !== 'idle') {
                                    return;
                                }
                                if (!configured.validate()) {
                                    return;
                                }
                                const { declined, predicted, selection } =
                                    configured.prepareSubmission(
                                        data.postcode?.value?.region ?? null,
                                    );
                                // Declined by a message rule: the notice is already on
                                // screen; there is nothing to send.
                                if (declined) {
                                    return;
                                }
                                submittedEmailRef.current =
                                    isLoggedIn && user.Email
                                        ? user.Email
                                        : data.email;
                                pendingBookingRef.current =
                                    predicted?.destination === 'Calendar' &&
                                    predicted.calendarSessionId
                                        ? {
                                              sessionId:
                                                  predicted.calendarSessionId,
                                              clientName: data.name,
                                              clientEmail:
                                                  isLoggedIn && user.Email
                                                      ? user.Email
                                                      : data.email,
                                          }
                                        : null;
                                if (preview) {
                                    preview.onSubmit();
                                    return;
                                }
                                // Files restored from an earlier session must not ride along
                                // on a form that never offered the attachment control.
                                submitEnquiry(
                                    data,
                                    allowsAttachments ? files : [],
                                    fieldSelection,
                                    selection,
                                );
                            },
                            // A submit click blocked by validation is a funnel
                            // step of its own — without it, users stuck on a
                            // failing field are indistinguishable from users
                            // who abandoned voluntarily.
                            (errors) => {
                                // Show every problem at once, not just the
                                // schema's: otherwise fixing the message only
                                // reveals the next blocker on the next click.
                                configured.validate();
                                // Field names only — never values. ExtraData
                                // carries them into the warehouse.
                                const fields = Object.keys(errors).join(',');
                                tracker.log('EnquiryValidationError', {
                                    url: new URL(window.location.href),
                                    data: {
                                        Source: source,
                                        fields,
                                        ExtraData: JSON.stringify({ fields }),
                                    },
                                });
                            },
                        )}
                        className="grid grid-cols-1 gap-x-4 gap-y-6 md:grid-cols-2"
                    >
                        <EnquiryProgressBar
                            form={form}
                            includeBranch={
                                includeBranch &&
                                !!professional.branchOptions?.length
                            }
                            collectLocation={collectLocation}
                            includeMessage={messageRequired}
                            requiredCustomFields={requiredCustomFields}
                            fieldAnswers={fieldAnswers}
                            includeEnquiryType={enquiryTypeOptions.length > 0}
                            enquiryTypeChosen={!!selectedType}
                            includeEnquirerType={asksEnquirerType}
                            enquirerTypeChosen={enquirerType !== ''}
                            includeProjectKind={asksProjectKind}
                            projectKindChosen={projectKind !== ''}
                        />
                        <FormField
                            name="name"
                            control={form.control}
                            render={({ field, fieldState }) => (
                                <FormItem>
                                    <FormLabel className="sr-only">
                                        Full Name
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            className="bg-[#fff] transition-colors duration-300 focus-visible:border-black"
                                            text="default"
                                            color="lightOutline"
                                            focus="none"
                                            placeholder="Full Name*"
                                            autoComplete="name"
                                            postfix={
                                                <FieldCompletionTick
                                                    visible={
                                                        !!field.value?.trim() &&
                                                        !fieldState.error
                                                    }
                                                />
                                            }
                                            // Don't autofocus in-page or it will hijack
                                            // the auto-submission enquiry session.
                                            // The last input with autoFocus={true} will be focused.
                                            autoFocus={
                                                isModal &&
                                                !disableAutoFocus &&
                                                !field.value
                                            }
                                            {...field}
                                            onBlur={() => {
                                                field.onBlur();
                                                saveEnquirySession(
                                                    form.getValues(),
                                                );
                                            }}
                                            onFocus={(e) => {
                                                e.target.select?.();
                                            }}
                                        />
                                    </FormControl>
                                    {(fieldState.isTouched ||
                                        form.formState.isSubmitted) && (
                                        <FormMessage variant="darker" />
                                    )}
                                </FormItem>
                            )}
                        />
                        <FormField
                            name="email"
                            control={form.control}
                            render={({ field, fieldState }) => (
                                <FormItem>
                                    <FormLabel className="sr-only">
                                        Email
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            className="bg-[#fff] transition-colors duration-300 focus-visible:border-black"
                                            type="email"
                                            text="default"
                                            color="lightOutline"
                                            focus="none"
                                            placeholder="Email*"
                                            autoComplete="email"
                                            postfix={
                                                <FieldCompletionTick
                                                    visible={
                                                        !!field.value?.trim() &&
                                                        !fieldState.error
                                                    }
                                                />
                                            }
                                            autoFocus={
                                                isModal &&
                                                !disableAutoFocus &&
                                                !isLoggedIn &&
                                                !!form.getValues('name') &&
                                                !field.value
                                            }
                                            disabled={
                                                isLoggedIn && !!user.Email
                                            }
                                            {...field}
                                            onBlur={() => {
                                                field.onBlur();
                                                saveEnquirySession(
                                                    form.getValues(),
                                                );
                                            }}
                                            onFocus={(e) => {
                                                e.target.select?.();
                                            }}
                                        />
                                    </FormControl>
                                    {(fieldState.isTouched ||
                                        form.formState.isSubmitted) && (
                                        <FormMessage variant="darker" />
                                    )}
                                </FormItem>
                            )}
                        />
                        <FormField
                            name="phoneNumber"
                            control={form.control}
                            render={({ field, fieldState }) => (
                                <FormItem>
                                    <FormLabel className="sr-only">
                                        Phone
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            className="bg-[#fff] transition-colors duration-300 focus-visible:border-black"
                                            type="tel"
                                            text="default"
                                            color="lightOutline"
                                            focus="none"
                                            placeholder="Phone*"
                                            autoComplete="tel"
                                            postfix={
                                                <FieldCompletionTick
                                                    visible={
                                                        !!field.value?.trim() &&
                                                        !fieldState.error
                                                    }
                                                />
                                            }
                                            autoFocus={
                                                isModal &&
                                                !disableAutoFocus &&
                                                !!form.getValues('name') &&
                                                !!form.getValues('email') &&
                                                !field.value
                                            }
                                            {...field}
                                            onBlur={() => {
                                                field.onBlur();
                                                saveEnquirySession(
                                                    form.getValues(),
                                                );
                                            }}
                                            onFocus={(e) => {
                                                e.target.select?.();
                                            }}
                                        />
                                    </FormControl>
                                    {(fieldState.isTouched ||
                                        form.formState.isSubmitted) && (
                                        <FormMessage variant="darker" />
                                    )}
                                </FormItem>
                            )}
                        />
                        {collectLocation && (
                            <FormField
                                name="postcode"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <FormItem>
                                        <FormLabel className="sr-only">
                                            {locationLabel}
                                        </FormLabel>
                                        <div className="relative">
                                            <FormControl>
                                                <Input
                                                    className={cn(
                                                        'bg-[#fff] transition-colors duration-300 focus-visible:border-black',
                                                        postcodeSearch
                                                            ? 'pr-16'
                                                            : 'pr-9',
                                                    )}
                                                    text="default"
                                                    color="lightOutline"
                                                    focus="none"
                                                    placeholder={`${locationLabel}*`}
                                                    // Its own suburb suggestions
                                                    // sit under this field, so the
                                                    // browser list must not cover
                                                    // them. `one-time-code` used to
                                                    // do that by claiming to be an
                                                    // SMS code field, which made
                                                    // iOS offer codes from Messages.
                                                    autoComplete="off"
                                                    autoFocus={
                                                        isModal &&
                                                        !disableAutoFocus &&
                                                        !!form.getValues(
                                                            'name',
                                                        ) &&
                                                        !!form.getValues(
                                                            'email',
                                                        ) &&
                                                        !!form.getValues(
                                                            'phoneNumber',
                                                        ) &&
                                                        !field.value
                                                    }
                                                    data-enquiry-filled={
                                                        field.value
                                                            ? 'true'
                                                            : 'false'
                                                    }
                                                    maxLength={100}
                                                    {...field}
                                                    ref={(el) => {
                                                        postcodeInputRef.current =
                                                            el;
                                                        field.ref(el);
                                                    }}
                                                    value={postcodeSearch}
                                                    onChange={(e) => {
                                                        setPostcodeSearch(
                                                            e.target.value,
                                                        );
                                                        setHighlightedIndex(-1);
                                                        findPlace(
                                                            e.target.value,
                                                        );
                                                        if (e.target.value) {
                                                            setIsPostcodeSearchOpen(
                                                                true,
                                                            );
                                                        } else {
                                                            selectPostcode(
                                                                undefined,
                                                            );
                                                        }
                                                    }}
                                                    onFocus={(e) => {
                                                        if (postcodeSearch) {
                                                            setIsPostcodeSearchOpen(
                                                                true,
                                                            );
                                                            if (
                                                                !placeOptions.length
                                                            ) {
                                                                findPlace(
                                                                    postcodeSearch,
                                                                );
                                                            }
                                                        }
                                                        e.target.select?.();
                                                    }}
                                                    onBlur={() => {
                                                        field.onBlur();
                                                        autoSelectPostcode();
                                                        setIsPostcodeSearchOpen(
                                                            false,
                                                        );
                                                        setHighlightedIndex(-1);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (
                                                            !isPostcodeSearchOpen ||
                                                            !placeOptions.length
                                                        ) {
                                                            return;
                                                        }
                                                        switch (e.key) {
                                                            case 'ArrowDown':
                                                                e.preventDefault();
                                                                setHighlightedIndex(
                                                                    (prev) =>
                                                                        prev <
                                                                        placeOptions.length -
                                                                            1
                                                                            ? prev +
                                                                              1
                                                                            : 0,
                                                                );
                                                                break;
                                                            case 'ArrowUp':
                                                                e.preventDefault();
                                                                setHighlightedIndex(
                                                                    (prev) =>
                                                                        prev > 0
                                                                            ? prev -
                                                                              1
                                                                            : placeOptions.length -
                                                                              1,
                                                                );
                                                                break;
                                                            case 'Enter':
                                                                e.preventDefault();
                                                                if (
                                                                    highlightedIndex >=
                                                                        0 &&
                                                                    highlightedIndex <
                                                                        placeOptions.length
                                                                ) {
                                                                    selectPostcode(
                                                                        placeOptions[
                                                                            highlightedIndex
                                                                        ],
                                                                    );
                                                                } else {
                                                                    selectAvailablePostCode();
                                                                }
                                                                break;
                                                            case 'Escape':
                                                                setIsPostcodeSearchOpen(
                                                                    false,
                                                                );
                                                                setHighlightedIndex(
                                                                    -1,
                                                                );
                                                                break;
                                                        }
                                                    }}
                                                />
                                            </FormControl>
                                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center gap-1.5 pr-4">
                                                {postcodeSearch && (
                                                    <button
                                                        type="button"
                                                        tabIndex={-1}
                                                        className="pointer-events-auto cursor-pointer opacity-70 hover:opacity-100"
                                                        aria-label="Clear postcode"
                                                        onPointerDown={(e) =>
                                                            e.preventDefault()
                                                        }
                                                        onClick={() =>
                                                            selectPostcode(
                                                                undefined,
                                                            )
                                                        }
                                                    >
                                                        <X className="size-4.5" />
                                                    </button>
                                                )}
                                                <FieldCompletionTick
                                                    visible={
                                                        !!field.value &&
                                                        !fieldState.error
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <PostcodePopover
                                            open={isPostcodeSearchOpen}
                                            usePortal={!isModal}
                                            onOpenChange={(open) => {
                                                if (
                                                    !open &&
                                                    document.activeElement ===
                                                        postcodeInputRef.current
                                                ) {
                                                    return;
                                                }
                                                setIsPostcodeSearchOpen(open);
                                            }}
                                            value={field.value}
                                            placeOptions={placeOptions}
                                            isLoading={isPlaceOptionsLoading}
                                            highlightedIndex={highlightedIndex}
                                            onSelect={selectPostcode}
                                        >
                                            <div className="absolute">
                                                <PopoverAnchor className="-mt-2" />
                                            </div>
                                        </PostcodePopover>
                                        {(fieldState.isTouched ||
                                            form.formState.isSubmitted) && (
                                            <FormMessage variant="darker" />
                                        )}
                                    </FormItem>
                                )}
                            />
                        )}
                        {canIncludeProjectDetails && (
                            <FormField
                                name="buildHubId"
                                control={form.control}
                                render={({ field }) => (
                                    <BuildHubSelector
                                        value={field.value}
                                        onChange={(value) => {
                                            field.onChange(value);
                                            saveEnquirySession({
                                                ...form.getValues(),
                                                buildHubId: value,
                                            });
                                        }}
                                        disabled={field.disabled}
                                        onBlur={field.onBlur}
                                        buildHubs={buildHubs}
                                        isLoading={isBuildHubsLoading}
                                        fieldSelection={fieldSelection}
                                        onFieldSelectionChange={
                                            setFieldSelection
                                        }
                                        onRefetchBuildHubs={fetchBuildHubs}
                                    />
                                )}
                            />
                        )}
                        {includeBranch &&
                            !!professional.branchOptions?.length && (
                                <FormField
                                    name="branchId"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormItem className="md:col-span-2">
                                            <FormLabel className="sr-only">
                                                Branch
                                            </FormLabel>
                                            <Select
                                                onValueChange={(value) => {
                                                    field.onChange(value);
                                                    saveEnquirySession(
                                                        form.getValues(),
                                                    );
                                                }}
                                                value={field.value}
                                                disabled={field.disabled}
                                            >
                                                <FormControl>
                                                    <SelectTrigger
                                                        className="w-full bg-[#fff]"
                                                        color="lightOutline"
                                                    >
                                                        <SelectValue placeholder="Branch*" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent
                                                    className="bg-transparent backdrop-blur-2xl"
                                                    borderless
                                                >
                                                    {professional.branchOptions.map(
                                                        (branch) => (
                                                            <SelectItem
                                                                key={
                                                                    branch.value
                                                                        .branchId
                                                                }
                                                                className="py-4 pr-4 focus:bg-black/10"
                                                                value={branch.value.branchId.toString()}
                                                            >
                                                                {branch.header}
                                                            </SelectItem>
                                                        ),
                                                    )}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage variant="darker" />
                                        </FormItem>
                                    )}
                                />
                            )}
                        {customFields.length > 0 && (
                            <CustomFormFields
                                fields={customFields}
                                answers={fieldAnswers}
                                errors={configuredErrors.fields}
                                disabled={form.formState.disabled}
                                visitorCountry={configured.visitorCountry}
                                onChange={configured.setFieldAnswer}
                            />
                        )}
                        {asksEnquirerType && (
                            <QuestionSelect
                                className={
                                    asksProjectKind
                                        ? undefined
                                        : 'md:col-span-2'
                                }
                                label={FIELD_QUESTIONS.enquirerType}
                                value={enquirerType}
                                options={ENQUIRER_TYPE_OPTIONS}
                                error={configuredErrors.enquirerType}
                                disabled={form.formState.disabled}
                                onChange={(value) =>
                                    configured.setEnquirerType(
                                        value as EnquirerType,
                                    )
                                }
                            />
                        )}
                        {asksProjectKind && (
                            <QuestionSelect
                                className={
                                    asksEnquirerType
                                        ? undefined
                                        : 'md:col-span-2'
                                }
                                label={FIELD_QUESTIONS.projectKind}
                                value={projectKind}
                                options={PROJECT_KIND_OPTIONS}
                                error={configuredErrors.projectKind}
                                disabled={form.formState.disabled}
                                onChange={(value) =>
                                    configured.setProjectKind(
                                        value as EnquiryProjectKind,
                                    )
                                }
                            />
                        )}
                        {enquiryTypeOptions.length > 0 && (
                            <div className="space-y-3 md:col-span-2">
                                <Select
                                    onValueChange={configured.setEnquiryTypeId}
                                    value={answers.typeId}
                                    disabled={form.formState.disabled}
                                >
                                    <SelectTrigger
                                        className={cn(
                                            'w-full bg-[#fff]',
                                            typeError && 'border-red-600',
                                        )}
                                        color="lightOutline"
                                        aria-label="What is your enquiry about?"
                                        aria-invalid={typeError}
                                    >
                                        <SelectValue placeholder="What is your enquiry about?*" />
                                    </SelectTrigger>
                                    <SelectContent
                                        className="bg-transparent backdrop-blur-2xl"
                                        borderless
                                    >
                                        {enquiryTypeOptions.map((type) => (
                                            <SelectItem
                                                key={type.id}
                                                className="py-4 pr-4 focus:bg-black/10"
                                                value={String(type.id)}
                                            >
                                                {type.title}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {typeError && (
                                    <p className="text-sm text-red-600">
                                        {configuredErrors.enquiryType}
                                    </p>
                                )}
                            </div>
                        )}
                        {declineMessage !== null ? (
                            <DeclineNotice message={declineMessage} />
                        ) : (
                            <>
                                {messageEnabled ? (
                                    <FormField
                                        name="message"
                                        control={form.control}
                                        render={({ field, fieldState }) => (
                                            <FormItem className="md:col-span-2">
                                                <FormLabel className="sr-only">
                                                    Message
                                                </FormLabel>
                                                <div className="relative">
                                                    <FormControl>
                                                        <Textarea
                                                            className={cn(
                                                                'bg-[#fff] pr-8 transition-colors duration-300 focus-visible:border-black',
                                                                'transition-[min-height] duration-300 ease-in-out',
                                                                isMessageFocused
                                                                    ? 'min-h-[160px] md:min-h-[200px]'
                                                                    : 'min-h-[106px]',
                                                            )}
                                                            text="default"
                                                            color="lightOutline"
                                                            focusStyle="none"
                                                            placeholder={
                                                                messagePlaceholder ??
                                                                (messageRequired
                                                                    ? 'Message*'
                                                                    : 'Message')
                                                            }
                                                            autoComplete="off"
                                                            rows={
                                                                isDesktop
                                                                    ? 4
                                                                    : 3
                                                            }
                                                            autoFocus={
                                                                isModal &&
                                                                !disableAutoFocus &&
                                                                !!form.getValues(
                                                                    'name',
                                                                ) &&
                                                                !!form.getValues(
                                                                    'email',
                                                                ) &&
                                                                !!form.getValues(
                                                                    'phoneNumber',
                                                                ) &&
                                                                !!form.getValues(
                                                                    'postcode',
                                                                )
                                                            }
                                                            style={{
                                                                ...(isKeyboardVisible && {
                                                                    ...(!isMessageFocused && {
                                                                        minHeight:
                                                                            '120px',
                                                                    }),
                                                                    resize: 'none',
                                                                }),
                                                            }}
                                                            {...field}
                                                            onFocus={(e) => {
                                                                setIsMessageFocused(
                                                                    true,
                                                                );
                                                                const inputLength =
                                                                    e.target
                                                                        .value
                                                                        .length;
                                                                e.target.setSelectionRange(
                                                                    inputLength,
                                                                    inputLength,
                                                                );
                                                            }}
                                                            onBlur={() => {
                                                                setIsMessageFocused(
                                                                    false,
                                                                );
                                                                field.onBlur();
                                                                saveEnquirySession(
                                                                    form.getValues(),
                                                                );
                                                                if (
                                                                    areOtherFieldsFilled()
                                                                ) {
                                                                    setSubmitNudgeKey(
                                                                        (k) =>
                                                                            k +
                                                                            1,
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </FormControl>
                                                    <MessageCompletionIndicator
                                                        hasValue={
                                                            !!field.value?.trim()
                                                        }
                                                        isEdited={
                                                            !!form.formState
                                                                .dirtyFields
                                                                .message &&
                                                            !fieldState.error
                                                        }
                                                    />
                                                </div>
                                                {(fieldState.isTouched ||
                                                    form.formState
                                                        .isSubmitted) && (
                                                    <FormMessage variant="darker" />
                                                )}
                                            </FormItem>
                                        )}
                                    />
                                ) : null}
                                {beforeSubmit ? (
                                    <div className="md:col-span-2">
                                        {beforeSubmit}
                                    </div>
                                ) : null}
                                <div className="flex flex-row items-start md:col-span-2">
                                    {isDesktop && allowsAttachments && (
                                        <div>
                                            <FileAttachment
                                                files={files}
                                                onChange={handleFileChange}
                                                maxAttachments={5}
                                                disabled={
                                                    form.formState.disabled
                                                }
                                                variant={attachmentVariant}
                                            />
                                        </div>
                                    )}
                                    <div className="grow"></div>
                                    <div>
                                        <SubmitButton
                                            isSubmitting={
                                                form.formState.disabled
                                            }
                                            isSuccess={isSubmitSuccess}
                                            disabled={disableSubmit}
                                            isModal={isModal}
                                            submitButtonText={submitButtonText}
                                            forceAnimate={submitNudgeKey > 0}
                                            animationTrigger={submitNudgeKey}
                                        />
                                    </div>
                                </div>
                                {consentText ? (
                                    <p className="m-0 text-xs leading-relaxed text-gray-600 md:col-span-2">
                                        {consentText}
                                    </p>
                                ) : null}
                            </>
                        )}
                    </form>
                </Form>
            </div>
            {isAutoSubmitting && !isSubmitSuccess && (
                <div
                    className="absolute top-0 left-0 z-1 flex h-full w-full animate-in flex-col items-center justify-center overflow-hidden rounded-xl duration-300 fade-in"
                    role="status"
                    aria-live="polite"
                >
                    <img
                        src="/assets/website/ui/images/glassy-background.png"
                        alt=""
                        className="pointer-events-none absolute top-0 left-0 -z-1 h-full w-full object-cover select-none"
                    />
                    <Loader2 className="mb-4 size-10 animate-spin text-gray-600" />
                    <div className="text-lg font-semibold">
                        Sending your enquiry...
                    </div>
                </div>
            )}
            {showSuccessMessage && (
                <div
                    className={cn(
                        'absolute top-0 left-0 z-1 hidden h-full w-full flex-col overflow-hidden rounded-xl',
                        isSubmitSuccess &&
                            'flex animate-in duration-300 zoom-in-95 fade-in',
                    )}
                    role="alert"
                    aria-live="assertive"
                >
                    <img
                        src="/assets/website/ui/images/glassy-background.png"
                        alt=""
                        className="pointer-events-none absolute top-0 left-0 -z-1 h-full w-full object-cover select-none"
                    />
                    <div className="border-b border-white px-4 pt-6 pb-4 text-lg md:px-8">
                        Message sent
                    </div>
                    <div className="relative flex grow flex-col items-center justify-center p-4 text-center text-base md:p-8">
                        {/* Recreate video element on success to replay from beginning. */}
                        {isSubmitSuccess && (
                            <video
                                src="/assets/website/ui/videos/message-sent.mp4"
                                autoPlay
                                playsInline
                                muted
                                className="size-24 transform-gpu mix-blend-darken"
                            />
                        )}
                        <div className="animate-in duration-300 fade-in slide-in-from-bottom-2">
                            <div className="mb-2 text-xl font-semibold">
                                Thank you for your enquiry!
                            </div>
                            <div className="text-gray-600">
                                We will get back to you as soon as possible.
                            </div>
                            <div className="mt-1 text-gray-600">
                                A confirmation has been sent to{' '}
                                <span className="font-medium text-gray-900">
                                    {isLoggedIn
                                        ? user.Email
                                        : form.getValues('email')}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-x-4 border-t border-white px-4 py-4 md:gap-x-6 md:px-8">
                        <Button
                            color="white"
                            weight="normal"
                            className="px-6 py-2.5 md:px-8"
                            asChild
                        >
                            <RoccoLink
                                to={successInboxLink.to}
                                isNonRemixRoute={
                                    successInboxLink.isNonRemixRoute
                                }
                            >
                                View My Inbox
                                <ArrowRight className="ml-2 size-5" />
                            </RoccoLink>
                        </Button>
                        <Button
                            className="px-8 py-2.5 md:px-16"
                            onClick={() => {
                                onSuccessClose?.();
                                if (!onSuccessClose) {
                                    setIsSubmitSuccess(false);
                                }
                            }}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

interface PendingBooking {
    sessionId: number;
    clientName: string;
    clientEmail: string;
}

interface SubmitButtonProps {
    /** Whether the form is currently submitting. */
    isSubmitting: boolean;
    /** Whether the submission was successful. */
    isSuccess: boolean;
    /** Externally disable the submit button. */
    disabled: boolean;
    /** Whether the form is inside a modal. */
    isModal: boolean;
    /** The default text for the submit button. */
    submitButtonText: string;
    /** Skip the IntersectionObserver and animate immediately on mount. */
    forceAnimate?: boolean;
    /** Increment to replay the attention animation without remounting. */
    animationTrigger?: number;
}

/**
 * Submit button with enhanced visual states:
 * - Default: shows submit text with an animated arrow
 * - Submitting: shows "Sending..." with a pulsing shimmer effect
 * - Success: briefly shows "Sent!" with a checkmark before the success overlay
 *
 * In modals the attention animation plays immediately on mount.
 * On-page, it plays once the button first scrolls into view.
 */
/**
 * Replaces the message box and submit button when the visitor's answers match a
 * routing rule that shows a message. Announced politely so screen readers hear the
 * form change under them.
 */
const DeclineNotice = ({ message }: { message: string }) => (
    <div
        role="status"
        aria-live="polite"
        className="animate-in rounded-md border border-black/10 bg-white/70 px-5 py-4 duration-300 fade-in md:col-span-2"
    >
        <p className="m-0 text-base leading-relaxed whitespace-pre-wrap text-gray-900">
            {message}
        </p>
        <p className="m-0 mt-2 text-sm text-gray-600">
            Change your answers above if this does not sound like your project.
        </p>
    </div>
);

export const SubmitButton = ({
    isSubmitting,
    isSuccess,
    disabled,
    isModal,
    submitButtonText,
    forceAnimate = false,
    animationTrigger = 0,
}: SubmitButtonProps) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [hasEnteredView, setHasEnteredView] = useState(forceAnimate);
    const [isAttentionAnimationActive, setIsAttentionAnimationActive] =
        useState(false);
    const [isArrowAnimationActive, setIsArrowAnimationActive] = useState(false);
    const animationFrameRef = useRef<number>(undefined);
    const animationCommitFrameRef = useRef<number>(undefined);

    const replayAttentionAnimation = useCallback(() => {
        window.cancelAnimationFrame(animationFrameRef.current ?? 0);
        window.cancelAnimationFrame(animationCommitFrameRef.current ?? 0);
        setIsAttentionAnimationActive(false);
        setIsArrowAnimationActive(false);

        animationFrameRef.current = window.requestAnimationFrame(() => {
            animationCommitFrameRef.current = window.requestAnimationFrame(
                () => {
                    setIsAttentionAnimationActive(true);
                    setIsArrowAnimationActive(true);
                },
            );
        });
    }, []);

    useEffect(() => {
        if (forceAnimate) {
            setHasEnteredView(true);
        }
    }, [forceAnimate]);

    useEffect(() => {
        if (isModal) {
            replayAttentionAnimation();
        }
    }, [isModal, replayAttentionAnimation]);

    useEffect(() => {
        if (!animationTrigger) {
            return;
        }

        setHasEnteredView(true);
        replayAttentionAnimation();
    }, [animationTrigger, replayAttentionAnimation]);

    useEffect(() => {
        if (isModal || !buttonRef.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) {
                    setHasEnteredView(true);
                    replayAttentionAnimation();
                    observer.disconnect();
                }
            },
            { threshold: 0.5 },
        );

        observer.observe(buttonRef.current);
        return () => observer.disconnect();
    }, [isModal, replayAttentionAnimation]);

    useEffect(() => {
        return () => {
            window.cancelAnimationFrame(animationFrameRef.current ?? 0);
            window.cancelAnimationFrame(animationCommitFrameRef.current ?? 0);
        };
    }, []);

    const canAnimate = !isSubmitting && !isSuccess;

    const buttonText = isSuccess
        ? 'Sent!'
        : isSubmitting
          ? 'Sending...'
          : submitButtonText;

    return (
        <Button
            ref={buttonRef}
            type="submit"
            className={cn(
                'group relative overflow-hidden px-9 py-2.5',
                'transition-all duration-300',
                isSubmitting && 'animate-pulse',
                canAnimate &&
                    isModal &&
                    isAttentionAnimationActive &&
                    'animate-submit-attention',
                canAnimate &&
                    !isModal &&
                    hasEnteredView &&
                    isAttentionAnimationActive &&
                    'animate-submit-attention-subtle',
            )}
            disabled={disabled || isSubmitting}
            isLoading={isSubmitting}
        >
            {buttonText}
            {isSuccess ? (
                <CheckCircle2 className="ml-2 size-5 animate-in fade-in zoom-in" />
            ) : (
                !isSubmitting && (
                    <AnimatedSubmitArrow
                        isAnimated={
                            (isModal || hasEnteredView) &&
                            isArrowAnimationActive
                        }
                    />
                )
            )}
        </Button>
    );
};

interface AnimatedSubmitArrowProps {
    /** Whether the animation should play on mount. */
    isAnimated?: boolean;
}

/**
 * An arrow icon for the submit button that plays a subtle nudge animation
 * on mount to draw attention to the button. On hover, the arrow shifts right
 * and stays there until the mouse leaves.
 *
 * Note: The parent button must have `className="group"` for the hover effect to work.
 */
const AnimatedSubmitArrow = ({
    isAnimated = false,
}: AnimatedSubmitArrowProps) => {
    return (
        <ArrowRight
            className={cn(
                'ml-2 size-5 transition-transform duration-300 ease-in-out',
                'group-hover:translate-x-1',
                isAnimated && 'animate-arrow-nudge',
            )}
        />
    );
};

/**
 * Field completion indicator: pencil when empty (inviting input),
 * green tick when filled (confirming completion).
 */
const FieldCompletionTick = ({ visible }: { visible: boolean }) => {
    if (visible) {
        return <Check className="size-4 animate-tick-pop-in text-[#16a34a]" />;
    }
    return <Pencil className="size-3.5 text-gray-400" />;
};

/**
 * Indicator for the message textarea. Shows a pencil when empty or pre-filled
 * (inviting user input), and a green tick once the user has edited it.
 */
const MessageCompletionIndicator = ({
    hasValue,
    isEdited,
}: {
    hasValue: boolean;
    isEdited: boolean;
}) => {
    return (
        <div className="pointer-events-none absolute top-3 right-3">
            {hasValue && isEdited ? (
                <Check className="size-4 animate-tick-pop-in text-[#16a34a]" />
            ) : (
                <Pencil className="size-3.5 text-gray-400" />
            )}
        </div>
    );
};

/** Required field names used for computing progress. */
const REQUIRED_FIELDS = ['name', 'email', 'phoneNumber'] as const;

interface EnquiryProgressBarProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any>;
    /** Include branch in the total field count. */
    includeBranch: boolean;
    /** Include the suburb / postcode in the total field count. */
    collectLocation: boolean;
    /** Include the message in the total field count (only when it is required). */
    includeMessage: boolean;
    /** Owner-defined questions that must be answered, with the answers so far. */
    requiredCustomFields?: PublicEnquiryFormField[];
    fieldAnswers?: Record<string, string>;
    /** The fixed qualifying questions, each counted only while the form asks it. */
    includeEnquiryType?: boolean;
    enquiryTypeChosen?: boolean;
    includeEnquirerType?: boolean;
    enquirerTypeChosen?: boolean;
    includeProjectKind?: boolean;
    projectKindChosen?: boolean;
}

/**
 * A thin progress bar that shows how many required fields are filled. Leverages
 * the endowed-progress effect by showing logged-in users start with fields
 * already completed. Celebrates when all fields are complete.
 */
const EnquiryProgressBar = ({
    form,
    includeBranch,
    collectLocation,
    includeMessage,
    requiredCustomFields = [],
    fieldAnswers = {},
    includeEnquiryType = false,
    enquiryTypeChosen = false,
    includeEnquirerType = false,
    enquirerTypeChosen = false,
    includeProjectKind = false,
    projectKindChosen = false,
}: EnquiryProgressBarProps) => {
    const values = form.watch();
    // Counted, not assumed: a field the form never renders can never be
    // filled, so leaving it in the total caps the bar below 100%. Everything
    // that can block submit is counted, so 100% really means ready.
    const totalFields =
        REQUIRED_FIELDS.length +
        (includeBranch ? 1 : 0) +
        (collectLocation ? 1 : 0) +
        (includeMessage ? 1 : 0) +
        (includeEnquiryType ? 1 : 0) +
        (includeEnquirerType ? 1 : 0) +
        (includeProjectKind ? 1 : 0) +
        requiredCustomFields.length;
    const filledCount = useMemo(() => {
        let count = 0;
        if (values.name?.trim()) count++;
        if (values.email?.trim()) count++;
        if (values.phoneNumber?.trim()) count++;
        if (collectLocation && values.postcode) count++;
        if (includeMessage && values.message?.trim()) count++;
        if (includeBranch && values.branchId) count++;
        if (includeEnquiryType && enquiryTypeChosen) count++;
        if (includeEnquirerType && enquirerTypeChosen) count++;
        if (includeProjectKind && projectKindChosen) count++;
        for (const field of requiredCustomFields) {
            if ((fieldAnswers[field.key] ?? '').trim()) count++;
        }
        return count;
    }, [
        values,
        includeBranch,
        collectLocation,
        includeMessage,
        includeEnquiryType,
        enquiryTypeChosen,
        includeEnquirerType,
        enquirerTypeChosen,
        includeProjectKind,
        projectKindChosen,
        requiredCustomFields,
        fieldAnswers,
    ]);

    const percentage = Math.round((filledCount / totalFields) * 100);
    const isComplete = percentage === 100;

    return (
        <div className="w-full md:col-span-2">
            <div
                className={cn(
                    'mb-1 flex items-center justify-between text-xs transition-colors duration-500',
                    isComplete ? 'font-medium text-black' : 'text-gray-500',
                )}
            >
                <span className="inline-flex items-center gap-1.5">
                    {isComplete && (
                        <CheckCircle2 className="size-3.5 animate-in duration-300 zoom-in" />
                    )}
                    {isComplete
                        ? 'Ready to submit!'
                        : `${filledCount} of ${totalFields} fields completed`}
                </span>
                <span>{percentage}%</span>
            </div>
            <div
                className={cn(
                    'w-full overflow-hidden rounded-full transition-[height,background-color] duration-500',
                    isComplete ? 'h-2 bg-gray-200' : 'h-1.5 bg-gray-200',
                )}
            >
                <div
                    className={cn(
                        'h-full rounded-full transition-[width] duration-700 ease-out',
                        'bg-black',
                    )}
                    style={{ width: `${percentage}%` }}
                />
            </div>
        </div>
    );
};

/** One of the form's optional qualifying dropdowns, styled like the enquiry type select. */
const QuestionSelect = ({
    className,
    label,
    value,
    options,
    error,
    disabled,
    onChange,
}: {
    className?: string;
    label: string;
    value: string;
    options: readonly { value: string; label: string }[];
    error: string | null;
    disabled?: boolean;
    onChange: (value: string) => void;
}) => (
    <div className={cn('space-y-3', className)}>
        <Select onValueChange={onChange} value={value} disabled={disabled}>
            <SelectTrigger
                className={cn('w-full bg-[#fff]', error && 'border-red-600')}
                color="lightOutline"
                aria-label={label}
                aria-invalid={!!error}
            >
                <SelectValue placeholder={`${label}*`} />
            </SelectTrigger>
            <SelectContent
                className="bg-transparent backdrop-blur-2xl"
                borderless
            >
                {options.map((option) => (
                    <SelectItem
                        key={option.value}
                        className="py-4 pr-4 focus:bg-black/10"
                        value={option.value}
                    >
                        {option.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
    </div>
);
